import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Container } from '../components/common/Container';
import { Button } from '../components/common/Button';
import { Link } from 'react-router-dom';
import { SimpleDemoComparison } from '../components/SimpleDemoComparison';
import { AdvancedDemoComparison } from '../components/AdvancedDemoComparison';
import { FossilFillsContainer } from '../components/fossil-fills/FossilFillsContainer';
import { FossilFillsDemo } from '../components/FossilFillsDemo';
import { ComparisonModeSelector } from '../components/ComparisonModeSelector';
import { useAuth } from '../contexts/AuthContext';

const DemoWrapper = styled.div`
  padding: ${theme.spacing['4xl']} 0;
  min-height: 80vh;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: ${theme.spacing['3xl']};
`;

const Title = styled.h1`
  font-size: ${theme.fontSizes['4xl']};
  margin-bottom: ${theme.spacing.lg};
  color: ${theme.colors.text.primary};
`;

const Subtitle = styled.p`
  font-size: ${theme.fontSizes.lg};
  color: ${theme.colors.text.secondary};
  max-width: 600px;
  margin: 0 auto;
`;

const TabSection = styled.div`
  margin-bottom: ${theme.spacing['3xl']};
`;

const TabButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: ${theme.spacing.md};
  margin-bottom: ${theme.spacing['2xl']};
  flex-wrap: wrap;
`;

const TabButton = styled.button<{ $active: boolean }>`
  padding: ${theme.spacing.md} ${theme.spacing.xl};
  background: ${props => props.$active ? theme.colors.primary : theme.colors.background};
  color: ${props => props.$active ? 'white' : theme.colors.text.primary};
  border: 2px solid ${props => props.$active ? theme.colors.primary : theme.colors.border};
  border-radius: ${theme.radii.md};
  font-size: ${theme.fontSizes.base};
  font-weight: ${props => props.$active ? '600' : '400'};
  cursor: pointer;
  transition: all ${theme.transitions.base};
  
  &:hover {
    background: ${props => props.$active ? theme.colors.primaryDark : theme.colors.backgroundSecondary};
    border-color: ${theme.colors.primary};
  }
`;


export const DemoPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'compare' | 'fossil'>('fossil');
  const [compareMode, setCompareMode] = useState<'prompts' | 'responses' | 'multi-llm'>('prompts');
  const auth = useAuth();
  const isAuthenticated = auth ? true : false;
  const userTier = (auth?.user as any)?.tier || 'free';
  
  // Determine which demos are available based on tier
  const canUseResponseComparison = isAuthenticated;
  const canUseMultiLLM = userTier === 'raptor' || userTier === 'rex';

  return (
    <DemoWrapper>
      <Container>
        <Header>
          <Title>Try Promptasaurus</Title>
          <Subtitle>
            Experience the power of AI-enhanced prompts. Test our tools and see how they transform your interactions with AI.
          </Subtitle>
        </Header>

        <TabSection>
          <TabButtons>
            <TabButton 
              $active={activeTab === 'fossil'} 
              onClick={() => setActiveTab('fossil')}
            >
              🦕 Try Fossil Fills
            </TabButton>
            <TabButton 
              $active={activeTab === 'compare'} 
              onClick={() => setActiveTab('compare')}
            >
              🔍 Compare Prompts
            </TabButton>
          </TabButtons>
        </TabSection>

        {activeTab === 'fossil' ? (
          // Use the actual Fossil Fills interactive experience
          <FossilFillsContainer />
        ) : (
          <>
            {/* Compare Prompts now shows the comparison between methods */}
            {compareMode === 'prompts' && (
              <FossilFillsDemo /> // This shows Original vs Promptasaurus vs Fossil Fills
            )}
            {compareMode === 'responses' && (
              <SimpleDemoComparison showResponsesOnly={true} />
            )}
            {compareMode === 'multi-llm' && (
              <AdvancedDemoComparison userTier={userTier} isAuthenticated={isAuthenticated} />
            )}
            
            {/* Show mode selector below results */}
            <ComparisonModeSelector
              currentMode={compareMode}
              onModeChange={setCompareMode}
              canUseResponseComparison={canUseResponseComparison}
              canUseMultiLLM={canUseMultiLLM}
              showResults={true}
            />
          </>
        )}
      </Container>
    </DemoWrapper>
  );
};